import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Upload, Search, X, ArrowRight, Database, RefreshCw, Plus,
  AlertCircle, Check, ChevronUp, ChevronDown, GitBranch,
  Trash2, TrendingUp, Building2, Layers, Globe, Users,
  ExternalLink, Edit2, ChevronRight, MapPin,
} from 'lucide-react';
import { crmApi } from '../services/api';
import { can } from '../lib/permissions';
import './CRM.css';

// ── Formatters ─────────────────────────────────────────────────────────────────

function fmtMoney(n) {
  if (n == null || n === '') return '—';
  const num = parseFloat(n);
  if (isNaN(num)) return '—';
  if (num >= 1_000_000_000) return `$${(num / 1_000_000_000).toFixed(1)}B`;
  if (num >= 1_000_000)     return `$${(num / 1_000_000).toFixed(1)}M`;
  if (num >= 1_000)         return `$${(num / 1_000).toFixed(0)}K`;
  return `$${num.toLocaleString()}`;
}

function fmtDate(d) {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }); }
  catch { return d; }
}

function fmtMult(n) {
  if (n == null) return '—';
  return `${parseFloat(n).toFixed(1)}x`;
}

function getStage(row) {
  if (row.participated_closed)    return { label: 'Closed',    color: '#16a34a' };
  if (row.participated_loi_terms) return { label: 'LOI/Terms', color: '#d97706' };
  if (row.participated_ioi)       return { label: 'IOI',       color: '#2563eb' };
  if (row.participated_nda_cim || row.participated_nda) return { label: 'NDA/CIM', color: '#7c3aed' };
  if (row.participated_contact)   return { label: 'Contact',   color: '#64748b' };
  return null;
}

function getDepartments(row) {
  return [
    ['pillar_tech_staff', 'Staff'], ['pillar_tech_gov', 'Gov'], ['pillar_tech_cyber', 'Cyber'],
    ['pillar_tech_data', 'Data'],   ['pillar_tech_erp', 'ERP'], ['pillar_tech_other', 'Tech+'],
    ['pillar_frca', 'FRCA'],        ['pillar_clin_solu', 'Clin.'], ['pillar_other', 'Other'],
  ].filter(([k]) => row[k]).map(([, l]) => l);
}

// ── Import wizard ──────────────────────────────────────────────────────────────

const STEP_LABELS = ['Upload File', 'Map Columns', 'Preview & Import'];

function StepBar({ step }) {
  return (
    <div className="crm-import-steps">
      {[0, 1, 2].map(i => (
        <React.Fragment key={i}>
          <div className={`crm-import-step ${step === i ? 'active' : step > i ? 'done' : ''}`}>
            <span className="crm-step-dot">{step > i ? <Check size={12} /> : i + 1}</span>
            <span>{STEP_LABELS[i]}</span>
          </div>
          {i < 2 && <div className={`crm-step-line ${step > i ? 'done' : ''}`} />}
        </React.Fragment>
      ))}
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────

const CRM = ({ onNavigate, currentUser, fetchDeals }) => {
  const canEdit   = can(currentUser, 'edit');
  const canImport = can(currentUser, 'import');

  // Navigation
  const [mainTab, setMainTab] = useState('companies'); // 'companies' | 'all_opps' | 'all_txns'
  const [view, setView]       = useState('table');     // 'table' | 'import'
  const [importStep, setImportStep] = useState(0);

  // Data
  const [companies, setCompanies]         = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [transactions, setTransactions]   = useState([]);
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState('');
  const [success, setSuccess]             = useState('');

  // Company detail
  const [selectedCompany, setSelectedCompany]   = useState(null);
  const [companyTab, setCompanyTab]             = useState('opportunities');
  const [companyOpps, setCompanyOpps]           = useState([]);
  const [companyTxns, setCompanyTxns]           = useState([]);
  const [companyLoading, setCompanyLoading]     = useState(false);

  // Record modal (add/edit opp or txn)
  const [recordModal, setRecordModal] = useState(null); // { type: 'opp'|'txn', mode: 'add'|'edit', data: {} }
  const [recordSaving, setRecordSaving] = useState(false);

  // Company modal (add/edit)
  const [companyModal, setCompanyModal] = useState(null); // { mode: 'add'|'edit', data: {} }
  const [companySaving, setCompanySaving] = useState(false);

  // Filters
  const [search, setSearch]           = useState('');
  const [companySearch, setCompanySearch] = useState('');
  const [filterStage, setFilterStage]   = useState('');
  const [filterPillar, setFilterPillar] = useState('');
  const [sortCol, setSortCol]           = useState('end_date');
  const [sortDir, setSortDir]           = useState('desc');

  // Import state
  const [importFile, setImportFile]               = useState(null);
  const [parseResult, setParseResult]             = useState(null);
  const [confirmedMappings, setConfirmedMappings] = useState({});
  const [importLoading, setImportLoading]         = useState(false);
  const [importError, setImportError]             = useState('');
  const [dragOver, setDragOver]                   = useState(false);

  const fileInputRef = useRef(null);

  // ── Fetch ──────────────────────────────────────────────────────────────────

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [coRes, oppRes, txnRes] = await Promise.all([
        crmApi.listCompanies(),
        crmApi.listOpportunities({ search, stage: filterStage, pillar: filterPillar }),
        crmApi.listTransactions({ search }),
      ]);
      setCompanies(coRes.data || []);
      setOpportunities(oppRes.data || []);
      setTransactions(txnRes.data || []);
    } catch {
      setError('Failed to load CRM data.');
    } finally {
      setLoading(false);
    }
  }, [search, filterStage, filterPillar]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Company-level opp/txn counts computed from already-loaded flat lists
  const countsByCompanyId = useMemo(() => {
    const map = {};
    for (const o of opportunities) {
      if (!o.company_id) continue;
      if (!map[o.company_id]) map[o.company_id] = { opps: 0, txns: 0 };
      map[o.company_id].opps++;
    }
    for (const t of transactions) {
      if (!t.company_id) continue;
      if (!map[t.company_id]) map[t.company_id] = { opps: 0, txns: 0 };
      map[t.company_id].txns++;
    }
    return map;
  }, [opportunities, transactions]);

  // ── Sorting ────────────────────────────────────────────────────────────────

  const sorted = useCallback((rows) => {
    if (!sortCol) return rows;
    return [...rows].sort((a, b) => {
      const cmp = String(a[sortCol] ?? '').localeCompare(String(b[sortCol] ?? ''), undefined, { numeric: true });
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [sortCol, sortDir]);

  const toggleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };

  const Th = ({ col, children }) => (
    <th className="crm-th-sortable" onClick={() => toggleSort(col)}>
      {children}
      {sortCol === col && (sortDir === 'asc' ? <ChevronUp size={11} /> : <ChevronDown size={11} />)}
    </th>
  );

  // ── Alerts ────────────────────────────────────────────────────────────────

  const showSuccess = (msg) => { setSuccess(msg); setTimeout(() => setSuccess(''), 4000); };

  // ── Company selection ──────────────────────────────────────────────────────

  const handleSelectCompany = useCallback(async (company) => {
    setSelectedCompany(company);
    setCompanyTab('opportunities');
    setCompanyLoading(true);
    try {
      const [oppRes, txnRes] = await Promise.all([
        crmApi.getCompanyOpportunities(company.id),
        crmApi.getCompanyTransactions(company.id),
      ]);
      setCompanyOpps(oppRes.data || []);
      setCompanyTxns(txnRes.data || []);
    } catch {
      setError('Failed to load company details.');
    } finally {
      setCompanyLoading(false);
    }
  }, []);

  // ── Actions ────────────────────────────────────────────────────────────────

  const handlePromote = async (id) => {
    try {
      await crmApi.promoteOpportunity(id);
      showSuccess('Promoted to deal successfully.');
      // Refresh global pipeline deals so the new deal appears in Pipeline
      if (fetchDeals) fetchDeals();
      fetchAll();
      if (selectedCompany) handleSelectCompany(selectedCompany);
      if (onNavigate) onNavigate('pipeline');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to promote.');
    }
  };

  const handleDeleteOpp = async (id) => {
    if (!window.confirm('Delete this opportunity?')) return;
    await crmApi.deleteOpportunity(id).catch(() => {});
    fetchAll();
    if (selectedCompany) handleSelectCompany(selectedCompany);
  };

  const handleDeleteTxn = async (id) => {
    if (!window.confirm('Delete this transaction?')) return;
    await crmApi.deleteTransaction(id).catch(() => {});
    fetchAll();
    if (selectedCompany) handleSelectCompany(selectedCompany);
  };

  const handleDeleteCompany = async (id) => {
    if (!window.confirm('Delete this company and unlink all its records?')) return;
    await crmApi.deleteCompany(id).catch(() => {});
    setSelectedCompany(null);
    fetchAll();
  };

  // ── Company modal helpers ───────────────────────────────────────────────────

  const openAddCompany = () => setCompanyModal({ mode: 'add', data: {} });
  const openEditCompany = (co) => setCompanyModal({ mode: 'edit', data: { ...co } });

  const handleCompanySave = async () => {
    if (!companyModal) return;
    if (!companyModal.data.name?.trim()) { alert('Company name is required.'); return; }
    setCompanySaving(true);
    try {
      const { mode, data } = companyModal;
      let saved;
      if (mode === 'add') {
        const res = await crmApi.createCompany(data);
        saved = res.data;
      } else {
        const res = await crmApi.updateCompany(data.id, data);
        saved = res.data;
      }
      setCompanyModal(null);
      fetchAll();
      if (saved) setSelectedCompany(saved);
    } catch (e) {
      alert('Save failed: ' + (e?.response?.data?.error || e.message));
    } finally {
      setCompanySaving(false);
    }
  };

  // ── Record modal helpers ────────────────────────────────────────────────────

  const openAddOpp = () => setRecordModal({
    type: 'opp', mode: 'add',
    data: { company_id: selectedCompany?.id, company_name: selectedCompany?.name }
  });
  const openAddTxn = () => setRecordModal({
    type: 'txn', mode: 'add',
    data: { company_id: selectedCompany?.id, company_name: selectedCompany?.name }
  });
  const openEditOpp = (row) => setRecordModal({ type: 'opp', mode: 'edit', data: { ...row } });
  const openEditTxn = (row) => setRecordModal({ type: 'txn', mode: 'edit', data: { ...row } });

  const handleRecordSave = async () => {
    if (!recordModal) return;
    setRecordSaving(true);
    try {
      const { type, mode, data } = recordModal;
      const isOpp = type === 'opp';
      if (mode === 'add') {
        if (isOpp) await crmApi.createOpportunity(data);
        else        await crmApi.createTransaction(data);
      } else {
        if (isOpp) await crmApi.updateOpportunity(data.id, data);
        else        await crmApi.updateTransaction(data.id, data);
      }
      setRecordModal(null);
      fetchAll();
      if (selectedCompany) handleSelectCompany(selectedCompany);
    } catch (e) {
      alert('Save failed: ' + (e?.response?.data?.error || e.message));
    } finally {
      setRecordSaving(false);
    }
  };

  // ── Import ─────────────────────────────────────────────────────────────────

  const processFile = async (file) => {
    setImportFile(file);
    setImportError('');
    setImportLoading(true);
    try {
      const res = await crmApi.parse(file);
      setParseResult(res.data);
      const mappings = {};
      for (const sheet of res.data.sheets) {
        mappings[sheet.sheetName] = { _dataStartRow: sheet.dataStartRow, _recordType: sheet.recordType };
        for (const col of sheet.columns) {
          mappings[sheet.sheetName][col.colIndex] = col.mappedField;
        }
      }
      setConfirmedMappings(mappings);
      setImportStep(1);
    } catch (err) {
      setImportError(err.response?.data?.error || 'Failed to parse file.');
    } finally {
      setImportLoading(false);
    }
  };

  const handleImport = async () => {
    setImportLoading(true);
    setImportError('');
    try {
      const res = await crmApi.import(importFile, confirmedMappings);
      const { opportunitiesImported, transactionsImported, companiesUpserted } = res.data;
      showSuccess(
        `Imported ${opportunitiesImported} opportunities and ${transactionsImported} transactions` +
        (companiesUpserted ? ` across ${companiesUpserted} companies` : '') + '.'
      );
      setView('table');
      setImportStep(0);
      setImportFile(null);
      setParseResult(null);
      fetchAll();
    } catch (err) {
      setImportError(err.response?.data?.error || 'Import failed.');
    } finally {
      setImportLoading(false);
    }
  };

  const cancelImport = () => {
    setView('table'); setImportStep(0); setImportFile(null); setParseResult(null); setImportError('');
  };

  // Filtered company list (must be before any early return)
  const filteredCompanies = useMemo(() => {
    if (!companySearch) return companies;
    const q = companySearch.toLowerCase();
    return companies.filter(c =>
      c.name?.toLowerCase().includes(q) ||
      c.industry?.toLowerCase().includes(q) ||
      c.geography?.toLowerCase().includes(q)
    );
  }, [companies, companySearch]);

  // ── Import view ────────────────────────────────────────────────────────────

  if (view === 'import') {
    return (
      <div className="crm-page">
        <div className="crm-header">
          <button className="crm-back-btn" onClick={cancelImport}><X size={15} /> Cancel</button>
          <h2 className="crm-import-title">Import Deal Data</h2>
        </div>
        <StepBar step={importStep} />
        {importError && <div className="crm-import-error"><AlertCircle size={15} />{importError}</div>}

        {/* Step 0: Upload */}
        {importStep === 0 && (
          <div className="crm-upload-area">
            <div
              className={`crm-dropzone ${dragOver ? 'drag-over' : ''}`}
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={e => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files[0]; if (f) processFile(f); }}
              onClick={() => fileInputRef.current?.click()}
            >
              {importLoading ? (
                <div className="crm-spinner-wrap"><div className="crm-spinner" /><p>Analysing with AI…</p></div>
              ) : (
                <>
                  <Upload size={36} />
                  <h3>Drop your Excel or CSV file here</h3>
                  <p>AI will detect all columns, map them to database fields, and identify companies automatically.</p>
                  <button className="crm-browse-btn" onClick={e => { e.stopPropagation(); fileInputRef.current?.click(); }}>Browse Files</button>
                  <p className="crm-drop-hint">.xlsx, .xls, .csv · up to 20 MB</p>
                </>
              )}
              <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }}
                onChange={e => { if (e.target.files[0]) processFile(e.target.files[0]); }} />
            </div>

            <div className="crm-import-tips">
              <div className="crm-tips-label">What happens on import</div>
              <ul className="crm-tips-list">
                {[
                  ['AI reads every sheet', 'Gemini 2.5 Flash detects headers & maps columns to your database schema'],
                  ['Companies are created', 'Each unique company is upserted into your company database'],
                  ['Opportunities are linked', 'Each row is linked to its company for a unified view'],
                  ['Transactions separate', '"Market Transactions" sheets go to the transactions table'],
                  ['You review before saving', 'Confirm AI mappings before any data is written'],
                ].map(([label, desc]) => (
                  <li key={label} className="crm-tip-row">
                    <span className="crm-tip-check"><Check size={11} /></span>
                    <span><span className="crm-tip-label">{label}</span><span className="crm-tip-desc">{desc}</span></span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Step 1: Map columns */}
        {importStep === 1 && parseResult && (
          <div className="crm-map-area">
            <div className="crm-map-header">
              <h3>Review column mappings</h3>
              <p>AI has mapped your columns. Adjust any mapping using the dropdowns, then proceed to preview.</p>
            </div>

            {parseResult.sheets.map(sheet => (
              <div key={sheet.sheetName} className="crm-sheet-block">
                <div className="crm-sheet-header">
                  <span className="crm-sheet-name">{sheet.sheetName}</span>
                  <span className={`crm-sheet-type ${sheet.recordType}`}>
                    {sheet.recordType === 'transactions' ? 'Transactions' : 'Opportunities'}
                  </span>
                  <span className="crm-sheet-count">{sheet.totalRows} rows</span>
                </div>

                <div className="crm-map-table-wrap">
                  <table className="crm-map-table">
                    <thead>
                      <tr>
                        <th>Column from file</th>
                        <th>Sample values</th>
                        <th>Confidence</th>
                        <th>Maps to</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sheet.columns.map(col => {
                        const currentMapping = confirmedMappings[sheet.sheetName]?.[col.colIndex];
                        return (
                          <tr key={col.colIndex} className={!currentMapping ? 'crm-map-row-unmapped' : ''}>
                            <td className="crm-col-name">{col.rawHeader}</td>
                            <td className="crm-sample-vals">
                              {sheet.preview
                                .map(row => row[col.mappedField] ?? row.custom_fields?.[col.rawHeader])
                                .filter(v => v != null && v !== '')
                                .slice(0, 3).join(' · ') || '—'}
                            </td>
                            <td>
                              {col.mappedField ? (
                                <span className={`crm-conf-badge ${col.mappedBy === 'ai' ? 'ai' : col.confidence >= 0.9 ? 'high' : 'medium'}`}>
                                  {col.mappedBy === 'ai' ? 'AI' : col.confidence >= 0.9 ? 'Auto' : 'Review'}
                                </span>
                              ) : (
                                <span className="crm-conf-badge low">Unmapped</span>
                              )}
                            </td>
                            <td>
                              <select
                                className="crm-map-select"
                                value={currentMapping || ''}
                                onChange={e => setConfirmedMappings(prev => ({
                                  ...prev,
                                  [sheet.sheetName]: { ...prev[sheet.sheetName], [col.colIndex]: e.target.value || null },
                                }))}
                              >
                                <option value="">— custom field —</option>
                                <optgroup label="Company Profile">
                                  <option value="company_name">Company Name</option>
                                  <option value="industry">Industry</option>
                                  <option value="geography">Geography</option>
                                  <option value="top_geographies">Top Geographies</option>
                                  <option value="hq_location">HQ Location</option>
                                  <option value="country">Country</option>
                                  <option value="state">State</option>
                                  <option value="city">City</option>
                                  <option value="website">Website</option>
                                  <option value="employee_count">Employee Count</option>
                                  <option value="ownership_type">Ownership Type</option>
                                  <option value="legal_structure">Legal Structure</option>
                                  <option value="naics_code">NAICS Code</option>
                                </optgroup>
                                <optgroup label="Deal Info">
                                  <option value="code_name">Code Name</option>
                                  <option value="seller_name">Seller Name</option>
                                  <option value="banker_advisor">Banker / Advisor</option>
                                  <option value="end_date">End Date</option>
                                  <option value="deal_type">Deal Type</option>
                                  <option value="deal_status">Deal Status</option>
                                  <option value="deal_owner">Deal Owner</option>
                                  <option value="priority_level">Priority Level</option>
                                </optgroup>
                                <optgroup label="Participation">
                                  <option value="participated_contact">Contact</option>
                                  <option value="participated_nda_cim">NDA / CIM</option>
                                  <option value="participated_nda">NDA</option>
                                  <option value="participated_ioi">IOI</option>
                                  <option value="participated_loi_terms">LOI / Terms</option>
                                  <option value="participated_closed">Closed</option>
                                </optgroup>
                                <optgroup label="Financials">
                                  <option value="ltm_revenue">LTM Revenue</option>
                                  <option value="ltm_ebitda">LTM EBITDA</option>
                                  <option value="revenue">Revenue</option>
                                  <option value="ebitda">EBITDA</option>
                                  <option value="adjusted_ebitda">Adj. EBITDA</option>
                                  <option value="enterprise_value">Enterprise Value</option>
                                  <option value="purchase_price">Purchase Price</option>
                                  <option value="multiple_revenue">Rev. Multiple</option>
                                  <option value="multiple_ebitda">EBITDA Multiple</option>
                                </optgroup>
                                <optgroup label="Departments">
                                  <option value="pillar_tech_staff">Tech: Staff</option>
                                  <option value="pillar_tech_gov">Tech: Gov</option>
                                  <option value="pillar_tech_cyber">Tech: Cyber</option>
                                  <option value="pillar_tech_data">Tech: Data</option>
                                  <option value="pillar_tech_erp">Tech: ERP</option>
                                  <option value="pillar_frca">FRCA</option>
                                  <option value="pillar_clin_solu">Clinical Solutions</option>
                                  <option value="pillar_other">Other Department</option>
                                </optgroup>
                                <optgroup label="Other">
                                  <option value="notes">Notes</option>
                                  <option value="source_type">Source</option>
                                </optgroup>
                              </select>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}

            <div className="crm-map-actions">
              <button className="crm-btn-secondary" onClick={() => setImportStep(0)}>Back</button>
              <button className="crm-btn-primary" onClick={() => setImportStep(2)}>
                Preview &amp; Import <ArrowRight size={15} />
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Preview */}
        {importStep === 2 && parseResult && (
          <div className="crm-preview-area">
            <div className="crm-preview-header">
              <h3>Preview import</h3>
              <p>First 5 rows per sheet. Companies will be created or updated automatically.</p>
            </div>

            {parseResult.sheets.map(sheet => (
              <div key={sheet.sheetName} className="crm-sheet-block">
                <div className="crm-sheet-header">
                  <span className="crm-sheet-name">{sheet.sheetName}</span>
                  <span className={`crm-sheet-type ${sheet.recordType}`}>
                    {sheet.recordType === 'transactions' ? 'Transactions' : 'Opportunities'}
                  </span>
                  <span className="crm-sheet-count">{sheet.totalRows} rows</span>
                </div>

                <div className="crm-preview-table-wrap">
                  <table className="crm-preview-table">
                    <thead>
                      <tr>
                        {sheet.columns
                          .filter(col => confirmedMappings[sheet.sheetName]?.[col.colIndex] !== null)
                          .slice(0, 10)
                          .map(col => {
                            const mapped = confirmedMappings[sheet.sheetName]?.[col.colIndex];
                            return (
                              <th key={col.colIndex}>
                                <span className="crm-preview-orig">{col.rawHeader}</span>
                                {mapped && <span className="crm-preview-mapped">→ {mapped}</span>}
                              </th>
                            );
                          })}
                      </tr>
                    </thead>
                    <tbody>
                      {sheet.preview.map((row, i) => (
                        <tr key={i}>
                          {sheet.columns
                            .filter(col => confirmedMappings[sheet.sheetName]?.[col.colIndex] !== null)
                            .slice(0, 10)
                            .map(col => {
                              const mapped = confirmedMappings[sheet.sheetName]?.[col.colIndex];
                              const val = mapped ? row[mapped] : row.custom_fields?.[col.rawHeader];
                              return <td key={col.colIndex}>{val != null ? String(val) : '—'}</td>;
                            })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}

            <div className="crm-map-actions">
              <button className="crm-btn-secondary" onClick={() => setImportStep(1)}>Back</button>
              <button className="crm-btn-primary" onClick={handleImport} disabled={importLoading}>
                {importLoading
                  ? 'Importing…'
                  : `Import ${parseResult.sheets.reduce((s, sh) => s + sh.totalRows, 0)} Records`}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── Main table view ────────────────────────────────────────────────────────

  const oppRows = sorted(opportunities);
  const txnRows = sorted(transactions);

  return (
    <div className="crm-page">

      {/* Header */}
      <div className="crm-header">
        <div className="crm-title-row">
          <Database size={22} />
          <div>
            <h1>CRM</h1>
            <span className="crm-subtitle">
              {companies.length} companies · {opportunities.length} opportunities · {transactions.length} transactions
            </span>
          </div>
        </div>
        <div className="crm-header-actions">
          <button className="crm-btn-secondary" onClick={fetchAll} title="Refresh"><RefreshCw size={14} /></button>
          {canImport && (
            <button className="crm-btn-primary" onClick={() => setView('import')}>
              <Upload size={15} /> Import Data
            </button>
          )}
        </div>
      </div>

      {error   && <div className="crm-error"><AlertCircle size={15} />{error}</div>}
      {success && <div className="crm-success"><Check size={15} />{success}</div>}

      {/* Main tabs */}
      <div className="crm-tabs">
        <button className={`crm-tab ${mainTab === 'companies' ? 'active' : ''}`} onClick={() => setMainTab('companies')}>
          <Building2 size={15} /> Companies
          {companies.length > 0 && <span className="crm-tab-count">{companies.length}</span>}
        </button>
        <button className={`crm-tab ${mainTab === 'all_opps' ? 'active' : ''}`} onClick={() => setMainTab('all_opps')}>
          <Layers size={15} /> All Opportunities
          {opportunities.length > 0 && <span className="crm-tab-count">{opportunities.length}</span>}
        </button>
        <button className={`crm-tab ${mainTab === 'all_txns' ? 'active' : ''}`} onClick={() => setMainTab('all_txns')}>
          <TrendingUp size={15} /> All Transactions
          {transactions.length > 0 && <span className="crm-tab-count">{transactions.length}</span>}
        </button>
      </div>

      {loading && <div className="crm-loading">Loading…</div>}

      {/* ── Companies view ─────────────────────────────────────────────────── */}
      {!loading && mainTab === 'companies' && (
        <div className={`crm-companies-layout ${selectedCompany ? '' : 'no-detail'}`}>

          {/* Left panel: company list */}
          <div className="crm-company-list-panel">
            <div className="crm-company-list-search">
              <div className="crm-search-wrap">
                <Search size={13} className="crm-search-icon" />
                <input
                  type="text"
                  placeholder="Search companies…"
                  value={companySearch}
                  onChange={e => setCompanySearch(e.target.value)}
                  style={{ paddingRight: '2.5rem' }}
                  className="crm-search-input"
                />
                {companySearch && <button className="crm-search-clear" onClick={() => setCompanySearch('')}><X size={12} /></button>}
              </div>
              {canEdit && (
                <button className="crm-icon-btn crm-add-company-btn" onClick={openAddCompany} title="Add company">
                  <Plus size={15} />
                </button>
              )}
            </div>

            {filteredCompanies.length === 0 ? (
              <div className="crm-company-empty">
                <Building2 size={32} />
                <p>{companies.length === 0 ? 'No companies yet. Import data to get started.' : 'No companies match your search.'}</p>
                {companies.length === 0 && canImport && (
                  <button className="crm-btn-primary" onClick={() => setView('import')}>
                    <Upload size={14} /> Import Data
                  </button>
                )}
              </div>
            ) : (
              <div className="crm-company-list">
                {filteredCompanies.map(company => {
                  const counts = countsByCompanyId[company.id] || { opps: 0, txns: 0 };
                  return (
                    <div
                      key={company.id}
                      className={`crm-company-card ${selectedCompany?.id === company.id ? 'selected' : ''}`}
                      onClick={() => handleSelectCompany(company)}
                    >
                      <div className="crm-cc-name">{company.name}</div>
                      <div className="crm-cc-meta">
                        {company.industry && <span>{company.industry}</span>}
                        {company.geography && <span><MapPin size={10} />{company.geography}</span>}
                      </div>
                      <div className="crm-cc-footer">
                        {company.ownership_type && <span className="crm-cc-badge">{company.ownership_type}</span>}
                        {counts.opps > 0 && <span className="crm-cc-count opp">{counts.opps} opp{counts.opps !== 1 ? 's' : ''}</span>}
                        {counts.txns > 0 && <span className="crm-cc-count txn">{counts.txns} txn{counts.txns !== 1 ? 's' : ''}</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Right panel: company detail */}
          {selectedCompany && (
            <div className="crm-company-detail-panel">
              {/* Detail header */}
              <div className="crm-detail-header">
                <div>
                  <div className="crm-detail-title">{selectedCompany.name}</div>
                  {selectedCompany.industry && <div className="crm-detail-industry">{selectedCompany.industry}</div>}
                </div>
                <div className="crm-detail-header-actions">
                  {canEdit && <>
                    <button className="crm-icon-btn" onClick={() => openEditCompany(selectedCompany)} title="Edit company">
                      <Edit2 size={13} />
                    </button>
                    <button className="crm-delete-btn" onClick={() => handleDeleteCompany(selectedCompany.id)} title="Delete company">
                      <Trash2 size={13} />
                    </button>
                  </>}
                  <button className="crm-icon-btn" onClick={() => setSelectedCompany(null)} title="Close">
                    <X size={15} />
                  </button>
                </div>
              </div>

              {/* Meta grid */}
              <div className="crm-detail-meta-grid">
                {[
                  ['Geography',      selectedCompany.geography],
                  ['HQ Location',    selectedCompany.hq_location || [selectedCompany.city, selectedCompany.state, selectedCompany.country].filter(Boolean).join(', ')],
                  ['Website',        selectedCompany.website],
                  ['Employees',      selectedCompany.employee_count],
                  ['Ownership',      selectedCompany.ownership_type],
                  ['Legal Structure',selectedCompany.legal_structure],
                  ['NAICS',          selectedCompany.naics_code],
                  ['Top Geographies',selectedCompany.top_geographies],
                ].filter(([, v]) => v).map(([label, val]) => (
                  <div key={label} className="crm-meta-item">
                    <label>{label}</label>
                    {label === 'Website'
                      ? <a href={val.startsWith('http') ? val : `https://${val}`} target="_blank" rel="noreferrer" className="crm-meta-link"><ExternalLink size={11} />{val}</a>
                      : <span>{val}</span>
                    }
                  </div>
                ))}
              </div>

              {/* Sub-tabs */}
              <div className="crm-detail-tabs">
                <button
                  className={`crm-detail-tab ${companyTab === 'opportunities' ? 'active' : ''}`}
                  onClick={() => setCompanyTab('opportunities')}
                >
                  <Layers size={13} /> Opportunities
                  {companyOpps.length > 0 && <span className="crm-tab-count">{companyOpps.length}</span>}
                </button>
                <button
                  className={`crm-detail-tab ${companyTab === 'transactions' ? 'active' : ''}`}
                  onClick={() => setCompanyTab('transactions')}
                >
                  <TrendingUp size={13} /> Transactions
                  {companyTxns.length > 0 && <span className="crm-tab-count">{companyTxns.length}</span>}
                </button>
                <div style={{ flex: 1 }} />
                {canEdit && (
                  <button
                    className="crm-btn-primary crm-detail-add-btn"
                    onClick={companyTab === 'opportunities' ? openAddOpp : openAddTxn}
                  >
                    <Plus size={13} /> Add {companyTab === 'opportunities' ? 'Opportunity' : 'Transaction'}
                  </button>
                )}
              </div>

              {/* Detail content */}
              <div className="crm-detail-content">
                {companyLoading && <div className="crm-loading">Loading…</div>}

                {!companyLoading && companyTab === 'opportunities' && (
                  companyOpps.length === 0
                    ? <div className="crm-detail-empty"><Layers size={28} /><p>No opportunities for this company</p></div>
                    : <div className="crm-table-wrap crm-detail-table-wrap">
                        <table className="crm-table">
                          <thead>
                            <tr>
                              <th>Date</th>
                              <th>Code Name</th>
                              <th>Banker</th>
                              <th>Stage</th>
                              <th>LTM Rev.</th>
                              <th>LTM EBITDA</th>
                              <th>Departments</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {companyOpps.map(row => {
                              const stage = getStage(row);
                              const pillars = getDepartments(row);
                              return (
                                <tr key={row.id}>
                                  <td className="crm-td crm-td-date">{fmtDate(row.end_date)}</td>
                                  <td className="crm-td">{row.code_name || '—'}</td>
                                  <td className="crm-td">{row.banker_advisor || '—'}</td>
                                  <td className="crm-td">
                                    {stage
                                      ? <span className="crm-stage-badge" style={{ borderColor: stage.color, color: stage.color }}>{stage.label}</span>
                                      : '—'}
                                  </td>
                                  <td className="crm-td crm-td-num">{fmtMoney(row.ltm_revenue)}</td>
                                  <td className="crm-td crm-td-num">{fmtMoney(row.ltm_ebitda)}</td>
                                  <td className="crm-td">
                                    <div className="crm-pillars">
                                      {pillars.slice(0, 3).map(p => <span key={p} className="crm-pillar-tag">{p}</span>)}
                                      {pillars.length > 3 && <span className="crm-pillar-tag crm-pillar-more">+{pillars.length - 3}</span>}
                                    </div>
                                  </td>
                                  <td className="crm-td-actions">
                                    {row.deal_id
                                      ? <span className="crm-badge-promoted"><Check size={11} /> In Pipeline</span>
                                      : canEdit && <button className="crm-promote-btn" onClick={() => handlePromote(row.id)}><GitBranch size={13} /> Promote</button>}
                                    {canEdit && <>
                                      <button className="crm-icon-btn" onClick={() => openEditOpp(row)} title="Edit"><Edit2 size={13} /></button>
                                      <button className="crm-delete-btn" onClick={() => handleDeleteOpp(row.id)}><Trash2 size={13} /></button>
                                    </>}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                )}

                {!companyLoading && companyTab === 'transactions' && (
                  companyTxns.length === 0
                    ? <div className="crm-detail-empty"><TrendingUp size={28} /><p>No transactions for this company</p></div>
                    : <div className="crm-table-wrap crm-detail-table-wrap">
                        <table className="crm-table">
                          <thead>
                            <tr>
                              <th>Date</th>
                              <th>Seller</th>
                              <th>Banker</th>
                              <th>Purchase Price</th>
                              <th>LTM Rev.</th>
                              <th>Rev. Mult.</th>
                              <th>EBITDA Mult.</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {companyTxns.map(row => (
                              <tr key={row.id}>
                                <td className="crm-td crm-td-date">{fmtDate(row.end_date)}</td>
                                <td className="crm-td">{row.seller_name || '—'}</td>
                                <td className="crm-td">{row.banker_advisor || '—'}</td>
                                <td className="crm-td crm-td-num crm-td-highlight">{fmtMoney(row.purchase_price)}</td>
                                <td className="crm-td crm-td-num">{fmtMoney(row.ltm_revenue)}</td>
                                <td className="crm-td crm-td-num">{fmtMult(row.multiple_revenue)}</td>
                                <td className="crm-td crm-td-num">{fmtMult(row.multiple_ebitda)}</td>
                                <td className="crm-td-actions">
                                  {canEdit && <>
                                    <button className="crm-icon-btn" onClick={() => openEditTxn(row)} title="Edit"><Edit2 size={13} /></button>
                                    <button className="crm-delete-btn" onClick={() => handleDeleteTxn(row.id)}><Trash2 size={13} /></button>
                                  </>}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── All Opportunities view ─────────────────────────────────────────── */}
      {!loading && mainTab === 'all_opps' && (
        <>
          <div className="crm-toolbar">
            <div className="crm-search-wrap">
              <Search size={15} className="crm-search-icon" />
              <input type="text" placeholder="Search company, banker, seller…" value={search}
                onChange={e => setSearch(e.target.value)} className="crm-search-input" />
              {search && <button className="crm-search-clear" onClick={() => setSearch('')}><X size={13} /></button>}
            </div>
            <select className="crm-filter-select" value={filterStage} onChange={e => setFilterStage(e.target.value)}>
              <option value="">All stages</option>
              <option value="contact">Contact</option>
              <option value="nda">NDA / CIM</option>
              <option value="ioi">IOI</option>
              <option value="loi">LOI / Terms</option>
              <option value="closed">Closed</option>
            </select>
            <select className="crm-filter-select" value={filterPillar} onChange={e => setFilterPillar(e.target.value)}>
              <option value="">All departments</option>
              <option value="staff">Tech: Staff</option>
              <option value="gov">Tech: Gov</option>
              <option value="cyber">Tech: Cyber</option>
              <option value="data">Tech: Data</option>
              <option value="erp">Tech: ERP</option>
              <option value="frca">FRCA</option>
              <option value="clin_solu">Clinical Solutions</option>
            </select>
          </div>

          {oppRows.length === 0
            ? <div className="crm-empty"><Layers size={40} /><h3>No opportunities yet</h3><p>Import your Excel database to populate the CRM.</p>{canImport && <button className="crm-btn-primary" onClick={() => setView('import')}><Upload size={15} /> Import Data</button>}</div>
            : <div className="crm-table-wrap">
                <table className="crm-table">
                  <thead>
                    <tr>
                      <Th col="end_date">Date</Th>
                      <Th col="company_name">Company</Th>
                      <Th col="banker_advisor">Banker / Advisor</Th>
                      <th>Stage</th>
                      <Th col="ltm_revenue">LTM Rev.</Th>
                      <Th col="ltm_ebitda">LTM EBITDA</Th>
                      <th>Departments</th>
                      <Th col="top_geographies">Geography</Th>
                      <th className="crm-th-actions">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {oppRows.map(row => {
                      const stage = getStage(row);
                      const pillars = getDepartments(row);
                      const companyName = row.crm_companies?.name || row.company_name || row.code_name || '—';
                      return (
                        <tr key={row.id}>
                          <td className="crm-td crm-td-date">{fmtDate(row.end_date)}</td>
                          <td className="crm-td crm-td-name">
                            <div className="crm-name-cell">
                              <span
                                className="crm-company crm-company-link"
                                onClick={() => { setMainTab('companies'); row.company_id && handleSelectCompany({ id: row.company_id, name: companyName }); }}
                              >{companyName}</span>
                              {row.code_name && row.company_name && <span className="crm-code-name">{row.code_name}</span>}
                            </div>
                          </td>
                          <td className="crm-td">{row.banker_advisor || '—'}</td>
                          <td className="crm-td">
                            {stage ? <span className="crm-stage-badge" style={{ borderColor: stage.color, color: stage.color }}>{stage.label}</span> : '—'}
                          </td>
                          <td className="crm-td crm-td-num">{fmtMoney(row.ltm_revenue)}</td>
                          <td className="crm-td crm-td-num">{fmtMoney(row.ltm_ebitda)}</td>
                          <td className="crm-td">
                            <div className="crm-pillars">
                              {pillars.slice(0, 4).map(p => <span key={p} className="crm-pillar-tag">{p}</span>)}
                              {pillars.length > 4 && <span className="crm-pillar-tag crm-pillar-more">+{pillars.length - 4}</span>}
                            </div>
                          </td>
                          <td className="crm-td">{row.top_geographies || row.geography || '—'}</td>
                          <td className="crm-td-actions">
                            {row.deal_id
                              ? <span className="crm-badge-promoted"><Check size={11} /> In Pipeline</span>
                              : canEdit && <button className="crm-promote-btn" onClick={() => handlePromote(row.id)}><GitBranch size={13} /> Promote</button>}
                            {canEdit && <button className="crm-delete-btn" onClick={() => handleDeleteOpp(row.id)}><Trash2 size={13} /></button>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <div className="crm-table-footer">{oppRows.length} opportunities{(filterStage || filterPillar || search) && ' (filtered)'}</div>
              </div>
          }
        </>
      )}

      {/* ── All Transactions view ──────────────────────────────────────────── */}
      {!loading && mainTab === 'all_txns' && (
        <>
          <div className="crm-toolbar">
            <div className="crm-search-wrap">
              <Search size={15} className="crm-search-icon" />
              <input type="text" placeholder="Search company, banker, seller…" value={search}
                onChange={e => setSearch(e.target.value)} className="crm-search-input" />
              {search && <button className="crm-search-clear" onClick={() => setSearch('')}><X size={13} /></button>}
            </div>
          </div>

          {txnRows.length === 0
            ? <div className="crm-empty"><TrendingUp size={40} /><h3>No transactions yet</h3><p>Import your Excel database to populate the CRM.</p>{canImport && <button className="crm-btn-primary" onClick={() => setView('import')}><Upload size={15} /> Import Data</button>}</div>
            : <div className="crm-table-wrap">
                <table className="crm-table">
                  <thead>
                    <tr>
                      <Th col="end_date">Date</Th>
                      <Th col="company_name">Company</Th>
                      <Th col="seller_name">Seller / Buyer</Th>
                      <Th col="banker_advisor">Banker</Th>
                      <Th col="purchase_price">Purchase Price</Th>
                      <Th col="ltm_revenue">LTM Rev.</Th>
                      <Th col="ltm_ebitda">LTM EBITDA</Th>
                      <Th col="multiple_revenue">Rev. Mult.</Th>
                      <Th col="multiple_ebitda">EBITDA Mult.</Th>
                      <th className="crm-th-actions">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {txnRows.map(row => (
                      <tr key={row.id}>
                        <td className="crm-td crm-td-date">{fmtDate(row.end_date)}</td>
                        <td className="crm-td crm-td-name">
                          <span className="crm-company">{row.crm_companies?.name || row.company_name || '—'}</span>
                        </td>
                        <td className="crm-td">{row.seller_name || '—'}</td>
                        <td className="crm-td">{row.banker_advisor || '—'}</td>
                        <td className="crm-td crm-td-num crm-td-highlight">{fmtMoney(row.purchase_price)}</td>
                        <td className="crm-td crm-td-num">{fmtMoney(row.ltm_revenue)}</td>
                        <td className="crm-td crm-td-num">{fmtMoney(row.ltm_ebitda)}</td>
                        <td className="crm-td crm-td-num">{fmtMult(row.multiple_revenue)}</td>
                        <td className="crm-td crm-td-num">{fmtMult(row.multiple_ebitda)}</td>
                        <td className="crm-td-actions">
                          <button className="crm-delete-btn" onClick={() => handleDeleteTxn(row.id)}><Trash2 size={13} /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="crm-table-footer">{txnRows.length} transactions{search && ' (filtered)'}</div>
              </div>
          }
        </>
      )}
      {/* ── Company Modal (add / edit) ─────────────────────────────────── */}
      {companyModal && (
        <CompanyModal
          modal={companyModal}
          onChange={data => setCompanyModal(m => ({ ...m, data }))}
          onSave={handleCompanySave}
          onClose={() => setCompanyModal(null)}
          saving={companySaving}
        />
      )}

      {/* ── Record Modal (add / edit opp or txn) ─────────────────────────── */}
      {recordModal && (
        <RecordModal
          modal={recordModal}
          onChange={data => setRecordModal(m => ({ ...m, data }))}
          onSave={handleRecordSave}
          onClose={() => setRecordModal(null)}
          saving={recordSaving}
        />
      )}
    </div>
  );
};

// ── CompanyModal ────────────────────────────────────────────────────────────

const COMPANY_FORM_FIELDS = [
  { key: 'name',           label: 'Company Name *', type: 'text',   full: true },
  { key: 'industry',       label: 'Industry',        type: 'text' },
  { key: 'ownership_type', label: 'Ownership Type',  type: 'text' },
  { key: 'geography',      label: 'Geography',       type: 'text' },
  { key: 'hq_location',    label: 'HQ Location',     type: 'text' },
  { key: 'city',           label: 'City',            type: 'text' },
  { key: 'state',          label: 'State',           type: 'text' },
  { key: 'country',        label: 'Country',         type: 'text' },
  { key: 'website',        label: 'Website',         type: 'text' },
  { key: 'employee_count', label: 'Employee Count',  type: 'number' },
  { key: 'legal_structure',label: 'Legal Structure', type: 'text' },
  { key: 'naics_code',     label: 'NAICS Code',      type: 'text' },
  { key: 'top_geographies',label: 'Top Geographies', type: 'text' },
  { key: 'description',    label: 'Description',     type: 'textarea', full: true },
];

function CompanyModal({ modal, onChange, onSave, onClose, saving }) {
  const { mode, data } = modal;
  const set = (key, val) => onChange({ ...data, [key]: val });

  return (
    <div className="crm-modal-overlay" onClick={onClose}>
      <div className="crm-modal" onClick={e => e.stopPropagation()}>
        <div className="crm-modal-header">
          <h3>{mode === 'add' ? 'Add Company' : 'Edit Company'}</h3>
          <button className="crm-icon-btn" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="crm-modal-body">
          <div className="crm-modal-grid">
            {COMPANY_FORM_FIELDS.map(f => (
              <div key={f.key} className={`crm-modal-field${f.full ? ' crm-modal-field-full' : ''}`}>
                <label>{f.label}</label>
                {f.type === 'textarea'
                  ? <textarea rows={3} value={data[f.key] || ''} onChange={e => set(f.key, e.target.value)} />
                  : <input type={f.type === 'number' ? 'number' : 'text'}
                      value={data[f.key] || ''} onChange={e => set(f.key, e.target.value)} />
                }
              </div>
            ))}
          </div>
        </div>
        <div className="crm-modal-footer">
          <button className="crm-btn-secondary" onClick={onClose}>Cancel</button>
          <button className="crm-btn-primary" onClick={onSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── RecordModal ─────────────────────────────────────────────────────────────

const OPP_FORM_FIELDS = [
  { key: 'end_date',              label: 'Date',               type: 'date' },
  { key: 'code_name',             label: 'Code Name',          type: 'text' },
  { key: 'seller_name',           label: 'Seller Name',        type: 'text' },
  { key: 'banker_advisor',        label: 'Banker / Advisor',   type: 'text' },
  { key: 'ltm_revenue',           label: 'LTM Revenue ($)',    type: 'number' },
  { key: 'ltm_ebitda',            label: 'LTM EBITDA ($)',     type: 'number' },
  { key: 'enterprise_value',      label: 'Enterprise Value ($)',type: 'number' },
  { key: 'deal_size',             label: 'Deal Size ($)',      type: 'number' },
  { key: 'industry',              label: 'Industry',           type: 'text' },
  { key: 'geography',             label: 'Geography',          type: 'text' },
  { key: 'ownership_type',        label: 'Ownership Type',     type: 'text' },
  { key: 'deal_owner',            label: 'Deal Owner',         type: 'text' },
  { key: 'source_type',           label: 'Source',             type: 'text' },
  { key: 'next_step',             label: 'Next Step',          type: 'text' },
  { key: 'notes',                 label: 'Notes',              type: 'textarea' },
];

const TXN_FORM_FIELDS = [
  { key: 'end_date',              label: 'Date',               type: 'date' },
  { key: 'seller_name',           label: 'Seller / Target',    type: 'text' },
  { key: 'banker_advisor',        label: 'Banker / Advisor',   type: 'text' },
  { key: 'purchase_price',        label: 'Purchase Price ($)', type: 'number' },
  { key: 'ltm_revenue',           label: 'LTM Revenue ($)',    type: 'number' },
  { key: 'ltm_ebitda',            label: 'LTM EBITDA ($)',     type: 'number' },
  { key: 'multiple_revenue',      label: 'Revenue Multiple',   type: 'number' },
  { key: 'multiple_ebitda',       label: 'EBITDA Multiple',    type: 'number' },
  { key: 'enterprise_value',      label: 'Enterprise Value ($)',type: 'number' },
  { key: 'industry',              label: 'Industry',           type: 'text' },
  { key: 'geography',             label: 'Geography',          type: 'text' },
  { key: 'ownership_type',        label: 'Ownership Type',     type: 'text' },
  { key: 'notes',                 label: 'Notes',              type: 'textarea' },
];

const STAGE_FIELDS = [
  { key: 'participated_contact',   label: 'Contact' },
  { key: 'participated_nda_cim',   label: 'NDA / CIM' },
  { key: 'participated_ioi',       label: 'IOI' },
  { key: 'participated_loi_terms', label: 'LOI / Terms' },
  { key: 'participated_closed',    label: 'Closed' },
];

function RecordModal({ modal, onChange, onSave, onClose, saving }) {
  const { type, mode, data } = modal;
  const isOpp = type === 'opp';
  const fields = isOpp ? OPP_FORM_FIELDS : TXN_FORM_FIELDS;
  const title = `${mode === 'add' ? 'Add' : 'Edit'} ${isOpp ? 'Opportunity' : 'Transaction'}`;

  const set = (key, val) => onChange({ ...data, [key]: val });

  return (
    <div className="crm-modal-overlay" onClick={onClose}>
      <div className="crm-modal" onClick={e => e.stopPropagation()}>
        <div className="crm-modal-header">
          <h3>{title}</h3>
          <button className="crm-icon-btn" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="crm-modal-body">
          <div className="crm-modal-grid">
            {fields.map(f => (
              <div key={f.key} className={`crm-modal-field${f.type === 'textarea' ? ' crm-modal-field-full' : ''}`}>
                <label>{f.label}</label>
                {f.type === 'textarea'
                  ? <textarea rows={3} value={data[f.key] || ''} onChange={e => set(f.key, e.target.value)} />
                  : <input type={f.type === 'number' ? 'number' : f.type === 'date' ? 'date' : 'text'}
                      value={data[f.key] || ''} onChange={e => set(f.key, e.target.value)} />
                }
              </div>
            ))}
          </div>

          {isOpp && (
            <div className="crm-modal-section">
              <div className="crm-modal-section-label">Stage Participation</div>
              <div className="crm-modal-checks">
                {STAGE_FIELDS.map(f => (
                  <label key={f.key} className="crm-modal-check">
                    <input type="checkbox" checked={!!data[f.key]}
                      onChange={e => set(f.key, e.target.checked)} />
                    {f.label}
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="crm-modal-footer">
          <button className="crm-btn-secondary" onClick={onClose}>Cancel</button>
          <button className="crm-btn-primary" onClick={onSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default CRM;
